"use client";

import { useEffect } from "react";
import {
  planDeploymentReload,
  readDeploymentReloadMarker,
  reloadForFreshDeployment,
} from "@/utils/monitoring/staleDeploymentRecovery";

type BuildVersionResponse = {
  version?: unknown;
};

let checkInFlight = false;

async function reloadIfDeploymentChanged(): Promise<void> {
  if (checkInFlight) {
    return;
  }

  checkInFlight = true;

  try {
    const response = await fetch("/api/build-version", {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      return;
    }

    const body = (await response.json()) as BuildVersionResponse;
    const serverVersion = typeof body.version === "string" ? body.version : "";
    const plan = planDeploymentReload(
      process.env.NEXT_PUBLIC_APP_VERSION,
      serverVersion,
      readDeploymentReloadMarker(window.sessionStorage),
    );

    if (!plan) {
      return;
    }

    reloadForFreshDeployment(
      window.location,
      window.sessionStorage,
      plan.marker,
    );
  } finally {
    checkInFlight = false;
  }
}

async function checkDeploymentFreshness(): Promise<void> {
  try {
    await reloadIfDeploymentChanged();
  } catch {
    // A failed version check must not take the page down. The webpack
    // error listener still reloads if a stale chunk actually throws.
  }
}

export function DeploymentFreshness() {
  useEffect(() => {
    let cancelled = false;

    const check = () => {
      if (cancelled || document.visibilityState === "hidden") {
        return;
      }

      void checkDeploymentFreshness();
    };

    check();
    document.addEventListener("visibilitychange", check);
    window.addEventListener("pageshow", check);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("pageshow", check);
    };
  }, []);

  return null;
}
